import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';

function Customers({ showToast }) {
  const { customers: allCustomers } = useData();
  const { filterCustomersByBranch, currentUser, canViewAllBranches } = useAuth();
  
  // Filter customers by user's accessible branches
  const accessibleCustomers = filterCustomersByBranch(allCustomers);
  
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'table'

  const customerGroups = [
    { id: 1, code: 'VIP', name: 'VIP' },
    { id: 2, code: 'SME', name: 'Doanh nghiệp vừa và nhỏ' },
    { id: 3, code: 'CORP', name: 'Doanh nghiệp lớn' },
    { id: 4, code: 'RETAIL', name: 'Khách hàng cá nhân' },
    { id: 5, code: 'HIGH-RISK', name: 'Rủi ro cao' }
  ];

  const [customers, setCustomers] = useState([
    {
      id: 1,
      cif: 'CIF0001234',
      name: 'Nguyễn Văn An',
      type: 'Cá nhân',
      idNumber: '001234567890',
      phone: '0901234567',
      email: 'nguyenvanan@example.com',
      address: '123 Nguyễn Huệ, Q.1, TP.HCM',
      branch: 'CN-HCM',
      group: 1,
      riskLevel: 'Thấp',
      totalDebt: 2500000000,
      alertCount: 0,
      status: 'Hoạt động',
      createdDate: '15/01/2025'
    },
    {
      id: 2,
      cif: 'CIF0002345',
      name: 'Công ty TNHH Thương Mại ABC',
      type: 'Doanh nghiệp',
      idNumber: '0123456789',
      phone: '0281234567',
      email: 'contact@abc.com.vn',
      address: '456 Lê Lợi, Q.1, TP.HCM',
      branch: 'CN-HCM',
      group: 2,
      riskLevel: 'Trung bình',
      totalDebt: 15000000000,
      alertCount: 2,
      status: 'Hoạt động',
      createdDate: '10/02/2025'
    },
    {
      id: 3,
      cif: 'CIF0003456',
      name: 'Trần Thị Bình',
      type: 'Cá nhân',
      idNumber: '001234567891',
      phone: '0912345678',
      email: 'tranthib@example.com',
      address: '789 Trần Hưng Đạo, Hoàn Kiếm, Hà Nội',
      branch: 'CN-HN',
      group: 4,
      riskLevel: 'Thấp',
      totalDebt: 1200000000,
      alertCount: 0,
      status: 'Hoạt động',
      createdDate: '20/02/2025'
    },
    {
      id: 4,
      cif: 'CIF0004567',
      name: 'Tập đoàn XYZ',
      type: 'Doanh nghiệp',
      idNumber: '0234567890',
      phone: '0241234567',
      email: 'info@xyz.com.vn',
      address: '321 Lý Thường Kiệt, Hai Bà Trưng, Hà Nội',
      branch: 'CN-HN',
      group: 3,
      riskLevel: 'Cao',
      totalDebt: 85000000000,
      alertCount: 5,
      status: 'Cảnh báo',
      createdDate: '05/03/2025'
    },
    {
      id: 5,
      cif: 'CIF0005678',
      name: 'Lê Minh Cường',
      type: 'Cá nhân',
      idNumber: '001234567892',
      phone: '0923456789',
      email: 'leminhcuong@example.com',
      address: '654 Hải Phòng, Sơn Trà, Đà Nẵng',
      branch: 'CN-DN',
      group: 1,
      riskLevel: 'Thấp',
      totalDebt: 5000000000,
      alertCount: 0,
      status: 'Hoạt động',
      createdDate: '12/03/2025'
    },
    {
      id: 6,
      cif: 'CIF0006789',
      name: 'Công ty CP Đầu tư DEF',
      type: 'Doanh nghiệp',
      idNumber: '0345678901',
      phone: '0251234567',
      email: 'invest@def.vn',
      address: '987 Nguyễn Văn Linh, Q.7, TP.HCM',
      branch: 'CN-HCM',
      group: 5,
      riskLevel: 'Cao',
      totalDebt: 45000000000,
      alertCount: 8,
      status: 'Cảnh báo',
      createdDate: '28/03/2025'
    }
  ]);

  const [formData, setFormData] = useState({
    cif: '',
    name: '',
    type: 'Cá nhân',
    idNumber: '',
    phone: '',
    email: '',
    address: '',
    branch: 'CN-HCM',
    group: 4,
    riskLevel: 'Thấp',
    totalDebt: 0
  });

  // Use accessible customers from auth context (filtered by branch)
  // This ensures users only see customers from their managed branches
  const filteredCustomers = accessibleCustomers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchText.toLowerCase()) ||
      customer.code?.toLowerCase().includes(searchText.toLowerCase()) ||
      (customer.idNumber && customer.idNumber.includes(searchText));
    const matchesType = !typeFilter || customer.type === typeFilter;
    const matchesRisk = !riskFilter || customer.riskLevel === riskFilter;
    // Note: customer.group filtering removed as DataContext customers don't have group property
    return matchesSearch && matchesType && matchesRisk;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditingCustomer(null);
    setFormData({
      cif: '',
      name: '',
      type: 'Cá nhân',
      idNumber: '',
      phone: '',
      email: '',
      address: '',
      branch: 'CN-HCM',
      group: 4,
      riskLevel: 'Thấp',
      totalDebt: 0
    });
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      cif: customer.cif,
      name: customer.name,
      type: customer.type,
      idNumber: customer.idNumber,
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      branch: customer.branch,
      group: customer.group,
      riskLevel: customer.riskLevel,
      totalDebt: customer.totalDebt
    });
    setShowModal(true);
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!formData.cif || !formData.name || !formData.idNumber || !formData.phone) {
      showToast('Vui lòng điền đầy đủ thông tin bắt buộc', 'warning');
      return;
    }

    if (editingCustomer) {
      setCustomers(customers.map(customer =>
        customer.id === editingCustomer.id
          ? { ...customer, ...formData }
          : customer
      ));
      showToast('Đã cập nhật khách hàng thành công', 'success');
    } else {
      const newCustomer = {
        id: customers.length > 0 ? Math.max(...customers.map(c => c.id)) + 1 : 1,
        ...formData,
        alertCount: 0,
        status: 'Hoạt động',
        createdDate: new Date().toLocaleDateString('vi-VN')
      };
      setCustomers([...customers, newCustomer]);
      showToast('Đã tạo khách hàng mới thành công', 'success');
    }

    setShowModal(false);
  };

  const deleteCustomer = (customerId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa khách hàng này?')) {
      setCustomers(customers.filter(customer => customer.id !== customerId));
      showToast('Đã xóa khách hàng thành công', 'success');
    }
  };

  const getRiskBadgeClass = (risk) => {
    if (risk === 'Cao') return 'danger';
    if (risk === 'Trung bình') return 'warning';
    return 'success';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'Hoạt động') return 'success';
    if (status === 'Cảnh báo') return 'warning';
    return 'secondary';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const getGroupName = (groupId) => {
    return customerGroups.find(g => g.id === groupId)?.code || 'N/A';
  };

  const getTotalDebt = () => {
    return customers.reduce((sum, c) => sum + c.totalDebt, 0);
  };

  return (
    <div className="container-fluid p-4">
      <div className="page-header-sticky">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">
              <i className="bi bi-person-circle me-2"></i>
              Quản lý Khách hàng
            </h2>
            <p className="text-muted mb-0">Quản lý thông tin và theo dõi khách hàng</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <i className="bi bi-plus-circle me-1"></i>
            Thêm khách hàng mới
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Tìm kiếm CIF, tên, CMND..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Loại khách hàng</option>
                <option value="Cá nhân">Cá nhân</option>
                <option value="Doanh nghiệp">Doanh nghiệp</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
              >
                <option value="">Mức độ rủi ro</option>
                <option value="Thấp">Thấp</option>
                <option value="Trung bình">Trung bình</option>
                <option value="Cao">Cao</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
              >
                <option value="">Nhóm khách hàng</option>
                {customerGroups.map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-1">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchText('');
                  setTypeFilter('');
                  setRiskFilter('');
                  setGroupFilter('');
                  showToast('Đã xóa bộ lọc', 'info');
                }}
                title="Xóa lọc"
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="col-md-2">
              <div className="btn-group w-100" role="group">
                <button
                  className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}
                  title="Xem dạng lưới"
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('list')}
                  title="Xem dạng danh sách"
                >
                  <i className="bi bi-list-ul"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  title="Xem dạng bảng"
                >
                  <i className="bi bi-table"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="row mb-3 stagger-animation">
        <div className="col-md-3">
          <div className="card border-left-primary hover-lift">
            <div className="card-body">
              <div className="text-primary font-weight-bold mb-1">Tổng khách hàng</div>
              <div className="h5 mb-0">{customers.length}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-left-success hover-lift">
            <div className="card-body">
              <div className="text-success font-weight-bold mb-1">Cá nhân</div>
              <div className="h5 mb-0">{customers.filter(c => c.type === 'Cá nhân').length}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-left-info hover-lift">
            <div className="card-body">
              <div className="text-info font-weight-bold mb-1">Doanh nghiệp</div>
              <div className="h5 mb-0">{customers.filter(c => c.type === 'Doanh nghiệp').length}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-left-danger hover-lift">
            <div className="card-body">
              <div className="text-danger font-weight-bold mb-1">Rủi ro cao</div>
              <div className="h5 mb-0">{customers.filter(c => c.riskLevel === 'Cao').length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Total Debt Card */}
      <div className="card mb-3 bg-light animate-fade-in-up">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h5><i className="bi bi-cash-stack text-warning me-2"></i>Tổng dư nợ toàn hệ thống</h5>
              <h3 className="text-warning">{formatCurrency(getTotalDebt())}</h3>
            </div>
            <div className="col-md-6">
              <h5><i className="bi bi-exclamation-triangle text-danger me-2"></i>Cảnh báo</h5>
              <h3 className="text-danger">{customers.filter(c => c.status === 'Cảnh báo').length} khách hàng</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Display */}
      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="row g-4 stagger-animation">
          {filteredCustomers.length > 0 ? (
            filteredCustomers.map((customer) => (
              <div key={customer.id} className="col-lg-4 col-md-6">
                <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: `4px solid ${customer.riskLevel === 'Cao' ? '#dc3545' : customer.riskLevel === 'Trung bình' ? '#ffc107' : '#28a745'}`}}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-3">
                      <span className={`badge bg-${customer.type === 'Doanh nghiệp' ? 'info' : 'secondary'}`}>
                        {customer.type}
                      </span>
                      <span className={`badge bg-${getStatusBadgeClass(customer.status)}`}>
                        {customer.status}
                      </span>
                    </div>
                    <h5 className="card-title">{customer.name}</h5>
                    <p className="text-muted small mb-2"><strong>CIF:</strong> {customer.cif}</p>
                    <div className="mb-3">
                      <small><i className="bi bi-card-text me-1"></i>{customer.idNumber}</small><br/>
                      <small><i className="bi bi-telephone me-1"></i>{customer.phone}</small><br/>
                      <small><i className="bi bi-building me-1"></i>{customer.branch}</small><br/>
                      <small><i className="bi bi-tag me-1"></i>{getGroupName(customer.group)}</small>
                    </div>
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <div className="p-2 rounded text-center" style={{backgroundColor: '#e6ffe6', border: '1px solid #b3ffb3'}}>
                          <small className="text-success fw-bold">{formatCurrency(customer.totalDebt)}</small>
                          <div className="small text-muted">Dư nợ</div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2 rounded text-center" style={{backgroundColor: customer.alertCount > 0 ? '#fee' : '#e6ffe6', border: customer.alertCount > 0 ? '1px solid #fcc' : '1px solid #b3ffb3'}}>
                          <small className={`fw-bold ${customer.alertCount > 0 ? 'text-danger' : 'text-success'}`}>{customer.alertCount}</small>
                          <div className="small text-muted">Cảnh báo</div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-primary flex-fill" onClick={() => openEditModal(customer)} title="Chỉnh sửa">
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger flex-fill" onClick={() => deleteCustomer(customer.id)} title="Xóa">
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="text-center text-muted py-5">
                <i className="bi bi-inbox fs-1"></i>
                <p className="mt-2">Không tìm thấy khách hàng nào</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="row g-3 stagger-animation">
          {filteredCustomers.length > 0 ? (
            filteredCustomers.map((customer) => (
              <div key={customer.id} className="col-12">
                <div className="card border-0 shadow-sm hover-lift" style={{borderLeft: `4px solid ${customer.riskLevel === 'Cao' ? '#dc3545' : customer.riskLevel === 'Trung bình' ? '#ffc107' : '#28a745'}`}}>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="d-flex align-items-start gap-3">
                          <div className={`badge bg-${customer.type === 'Doanh nghiệp' ? 'info' : 'secondary'} align-self-start`}>
                            {customer.type}
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="mb-1">{customer.name}</h5>
                            <small className="text-muted d-block mb-2"><strong>CIF:</strong> {customer.cif}</small>
                            <div className="d-flex gap-3 flex-wrap">
                              <small><i className="bi bi-card-text me-1"></i>{customer.idNumber}</small>
                              <small><i className="bi bi-telephone me-1"></i>{customer.phone}</small>
                              <small><i className="bi bi-building me-1"></i>{customer.branch}</small>
                              <small><i className="bi bi-tag me-1"></i>{getGroupName(customer.group)}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="row g-2">
                          <div className="col-6">
                            <small className="text-muted d-block">Dư nợ</small>
                            <strong className="text-success">{formatCurrency(customer.totalDebt)}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Cảnh báo</small>
                            <span className={`badge bg-${customer.alertCount > 0 ? 'danger' : 'success'}`}>
                              {customer.alertCount}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="d-flex gap-2 justify-content-end">
                          <span className={`badge bg-${getRiskBadgeClass(customer.riskLevel)}`}>
                            {customer.riskLevel}
                          </span>
                          <span className={`badge bg-${getStatusBadgeClass(customer.status)}`}>
                            {customer.status}
                          </span>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEditModal(customer)} title="Chỉnh sửa">
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => deleteCustomer(customer.id)} title="Xóa">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="text-center text-muted py-5">
                <i className="bi bi-inbox fs-1"></i>
                <p className="mt-2">Không tìm thấy khách hàng nào</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>CIF</th>
                    <th>Tên khách hàng</th>
                    <th>Loại</th>
                    <th>CMND/MST</th>
                    <th>Chi nhánh</th>
                    <th>Nhóm</th>
                    <th>Dư nợ</th>
                    <th>Rủi ro</th>
                    <th>Cảnh báo</th>
                    <th>Trạng thái</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map((customer) => (
                      <tr key={customer.id}>
                        <td><strong>{customer.cif}</strong></td>
                        <td>{customer.name}</td>
                        <td>
                          <span className={`badge bg-${customer.type === 'Doanh nghiệp' ? 'info' : 'secondary'}`}>
                            {customer.type}
                          </span>
                        </td>
                        <td><small>{customer.idNumber}</small></td>
                        <td><span className="badge bg-primary">{customer.branch}</span></td>
                        <td><span className="badge bg-info">{getGroupName(customer.group)}</span></td>
                        <td><small className="text-primary">{formatCurrency(customer.totalDebt)}</small></td>
                        <td>
                          <span className={`badge bg-${getRiskBadgeClass(customer.riskLevel)}`}>
                            {customer.riskLevel}
                          </span>
                        </td>
                        <td>
                          {customer.alertCount > 0 ? (
                            <span className="badge bg-danger">{customer.alertCount}</span>
                          ) : (
                            <span className="badge bg-success">0</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge bg-${getStatusBadgeClass(customer.status)}`}>
                            {customer.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditModal(customer)}
                            title="Chỉnh sửa"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteCustomer(customer.id)}
                            title="Xóa"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center text-muted py-4">
                        <i className="bi bi-inbox fs-1"></i>
                        <p className="mt-2">Không tìm thấy khách hàng nào</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block animate-fade-in" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl animate-scale-in">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCustomer ? 'Chỉnh sửa khách hàng' : 'Thêm khách hàng mới'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Số CIF <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="cif"
                        className="form-control"
                        value={formData.cif}
                        onChange={handleInputChange}
                        placeholder="CIF0001234"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tên khách hàng <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Họ tên / Tên công ty"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Loại khách hàng</label>
                      <select
                        name="type"
                        className="form-select"
                        value={formData.type}
                        onChange={handleInputChange}
                      >
                        <option value="Cá nhân">Cá nhân</option>
                        <option value="Doanh nghiệp">Doanh nghiệp</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">CMND/MST <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="idNumber"
                        className="form-control"
                        value={formData.idNumber}
                        onChange={handleInputChange}
                        placeholder="001234567890"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Số điện thoại <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="0901234567"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Địa chỉ</label>
                      <input
                        type="text"
                        name="address"
                        className="form-control"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Địa chỉ đầy đủ"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Chi nhánh</label>
                      <select
                        name="branch"
                        className="form-select"
                        value={formData.branch}
                        onChange={handleInputChange}
                      >
                        <option value="CN-HN">CN-HN</option>
                        <option value="CN-HCM">CN-HCM</option>
                        <option value="CN-DN">CN-DN</option>
                        <option value="CN-HP">CN-HP</option>
                        <option value="CN-CT">CN-CT</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Nhóm khách hàng</label>
                      <select
                        name="group"
                        className="form-select"
                        value={formData.group}
                        onChange={handleInputChange}
                      >
                        {customerGroups.map(group => (
                          <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Mức độ rủi ro</label>
                      <select
                        name="riskLevel"
                        className="form-select"
                        value={formData.riskLevel}
                        onChange={handleInputChange}
                      >
                        <option value="Thấp">Thấp</option>
                        <option value="Trung bình">Trung bình</option>
                        <option value="Cao">Cao</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Tổng dư nợ (VNĐ)</label>
                      <input
                        type="number"
                        name="totalDebt"
                        className="form-control"
                        value={formData.totalDebt}
                        onChange={handleInputChange}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Hủy
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-save me-2"></i>
                    {editingCustomer ? 'Cập nhật' : 'Tạo mới'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;
